import { type FC } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip, Legend, type LegendItem, type ChartOptions } from 'chart.js';
import styles from './Doughnut.module.scss';
import type { DoughnutChartProps, DoughnutChartType } from './types/propType';

Chart.register(ArcElement, Tooltip, Legend);

export function defaultAfterUpdate(chart: Chart | DoughnutChartType, doughnutId: string) {
  // get the div in which we will add the legends
  let doughnutLegend = document.getElementById(doughnutId);
  if (!doughnutLegend) {
    doughnutLegend = document.getElementById('nextgen-doughnut-legend');
  }
  if (doughnutLegend) {
    doughnutLegend.innerHTML = '';
    // get legends from chart object
    const items: LegendItem[] | undefined = chart?.options?.plugins?.legend?.labels?.generateLabels?.(chart as Chart);

    // create ul element & set id as doughnut-chart-data-point
    const ulElement = document.createElement('ul');
    ulElement.setAttribute('id', 'doughnut-chart-data-point');

    // creating and adding li element into ul element to create legends for chart
    items?.forEach((item) => {
      const liElement = document.createElement('li');
      const labelElement = document.createElement('span');
      const labelValueElement = document.createElement('span');
      const bulletPointElement = document.createElement('span');
      bulletPointElement.classList.add(`${styles['doughnut-label-bullet']}`);
      bulletPointElement.style.backgroundColor = item.fillStyle as string;
      liElement.classList.add(`${styles['doughnut-legned-li']}`);
      labelElement.classList.add(`${styles['doughnut-label']}`);
      labelValueElement.classList.add(`${styles['doughnut-label']}`);
      labelElement.appendChild(document.createTextNode(item.text));
      chart.data.datasets[0]?.data.forEach((data, i: number) => {
        if (chart?.data?.labels?.[i] === item.text) {
          labelValueElement.appendChild(document.createTextNode(`${data}%`));
        }
      });
      labelElement.prepend(bulletPointElement);
      liElement.appendChild(labelElement);
      liElement.appendChild(labelValueElement);
      ulElement.appendChild(liElement);
    });

    // adding ul element into legend div
    doughnutLegend.appendChild(ulElement);
  }
}

const DoughnutChart: FC<DoughnutChartProps> = (props: DoughnutChartProps) => {
  const {
    doughnutId,
    data,
    htmlCustomPlugin,
    options,
    doughnutContainerClassName = '',
    legnedContainerClassName = '',
    mainContainerClassName = '',
  } = props;

  // default custom plugin to customize legend in doughnut chart (chart.js)
  const htmlLegendDefaultPlugin = [
    {
      id: 'htmlLegend',
      afterUpdate(chart: Chart) {
        defaultAfterUpdate(chart, doughnutId);
      },
    },
  ];

  const defaultOptions: ChartOptions<'doughnut'> = {
    cutout: '80%', // used to control the size of inner hole or cutout in doughnut chart
    elements: {
      arc: {
        // used to control the width of the border drawn around diffrent types of datapoint or elements in doughnut chart
        borderWidth: 3,
        borderRadius: 8, // used to modify the radius of doughnut chart
      },
    },
    responsive: true, // used to control responsive behaviour of doughnut chart
    plugins: {
      legend: { display: false }, // used to show chart js default legend in  doughnut chart
    },
  };

  return (
    <div
      data-testid="doughnut-chart-test-id"
      className={`${styles['doughnut-container']} ${mainContainerClassName ?? ''}`}
    >
      <div className={`${doughnutContainerClassName ?? ''}`}>
        <Doughnut
          data={data}
          options={options ?? defaultOptions}
          plugins={htmlCustomPlugin ?? htmlLegendDefaultPlugin}
        />
      </div>
      <div id={doughnutId ?? 'nextgen-doughnut-legend'} className={`${legnedContainerClassName ?? ''}`}></div>
    </div>
  );
};

export default DoughnutChart;
